import type { CheckIn, KeyResult, Objective, ObjectiveStatusT, Task } from '@portfolio-pm/shared';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  GitBranch,
  LayoutGrid,
  List,
  Map as MapIcon,
  Menu,
  Pencil,
  Plus,
  Rows3,
  X,
} from 'lucide-react';
import { type TouchEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getDocStore } from '../../store/doc-store.ts';
import { ScrollViewport } from '../layout/ScrollViewport.tsx';
import { hasSearchCriteria } from '../search/workspace-search.ts';
import { MobileOkrSelector } from './MobileOkrSelector.tsx';
import { OkrGridView, buildOkrGridRows } from './OkrGridView.tsx';
import { OkrStrategyMap } from './OkrStrategyMap.tsx';
import { OkrTaskDetail } from './OkrTaskDetail.tsx';
import { OkrTimeline } from './OkrTimeline.tsx';
import { CheckInCard, KeyResultEditor, ObjectiveEditor } from './okr-editors.tsx';
import {
  IconActionButton,
  ProgressBar,
  STATUS_OPTIONS,
  SectionHeading,
  StatusBadge,
  fieldClass,
  formatProgressMode,
  formatStatus,
  iconButtonClass,
  innerCardClass,
  labelClass,
  matchesMobileOkr,
  mutedClass,
  primaryButtonClass,
  secondaryButtonClass,
  selectClass,
  shellCardClass,
  titleClass,
} from './okr-ui.tsx';

type EditorState =
  | { kind: 'none' }
  | { kind: 'create-objective'; parentObjectiveId?: string }
  | { kind: 'edit-objective'; objectiveId: string }
  | { kind: 'create-kr'; objectiveId: string }
  | { kind: 'edit-kr'; keyResultId: string };

type OkrView = 'list' | 'grid' | 'map' | 'timeline';

export interface OkrWorkspaceProps {
  searchQuery?: string;
  onClearSearch?(): void;
  onOpenTask?(taskId: string): void;
}

export function OkrWorkspace({
  searchQuery = '',
  onClearSearch,
  onOpenTask,
}: OkrWorkspaceProps): JSX.Element {
  const store = getDocStore();
  const activeProject = store.useActiveProject();
  const snapshot = store.useOkrSnapshot();
  const projects = store.useProjects();

  const [editor, setEditor] = useState<EditorState>({ kind: 'none' });
  const [view, setView] = useState<OkrView>('list');
  const [selectedKrId, setSelectedKrId] = useState<string | undefined>(undefined);
  const [selectedTaskId, setSelectedTaskId] = useState<string | undefined>(undefined);
  const [expandedKrIds, setExpandedKrIds] = useState<Set<string>>(new Set());
  const [linkingKrId, setLinkingKrId] = useState<string | undefined>(undefined);
  const [linkTaskPick, setLinkTaskPick] = useState<string | undefined>(undefined);
  const [linkError, setLinkError] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<'all' | ObjectiveStatusT>('all');
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  const [mobileSelectorOpen, setMobileSelectorOpen] = useState(false);
  const mobileDetailTouchStartX = useRef<number | undefined>(undefined);
  const treeTouchStartRef = useRef<{ x: number; y: number } | undefined>(undefined);

  const previousProjectIdRef = useRef(activeProject.id);

  const [daily, setDaily] = useState({
    targetType: 'objective' as CheckIn['targetType'],
    targetId: '',
    note: '',
    blockers: '',
    nextSteps: '',
    statusUpdate: '' as '' | ObjectiveStatusT,
    confidenceUpdate: '',
  });

  const [weekly, setWeekly] = useState({
    targetType: 'objective' as CheckIn['targetType'],
    targetId: '',
    note: '',
    blockers: '',
    nextSteps: '',
    statusUpdate: '' as '' | ObjectiveStatusT,
    confidenceUpdate: '',
  });

  useEffect(() => {
    if (previousProjectIdRef.current === activeProject.id) return;
    previousProjectIdRef.current = activeProject.id;
    setEditor({ kind: 'none' });
    setSelectedKrId(undefined);
    setSelectedTaskId(undefined);
    setExpandedKrIds(new Set());
    setLinkingKrId(undefined);
    setLinkTaskPick(undefined);
    setLinkError(undefined);
    setMobileDetailOpen(false);
  }, [activeProject.id]);

  useEffect(() => {
    const handler = (event: Event): void => {
      const nextView = (event as CustomEvent<OkrView>).detail;
      if (!['list', 'grid', 'map', 'timeline'].includes(nextView)) return;
      setView(nextView);
    };
    window.addEventListener('portfolio-okr-view', handler);
    return () => window.removeEventListener('portfolio-okr-view', handler);
  }, []);

  useEffect(() => {
    const hasContent =
      editor.kind !== 'none' || selectedKrId !== undefined || selectedTaskId !== undefined;
    if (hasContent && matchesMobileOkr()) setMobileDetailOpen(true);
  }, [editor.kind, selectedKrId, selectedTaskId]);

  function handleDetailTouchStart(e: TouchEvent<HTMLDivElement>): void {
    mobileDetailTouchStartX.current = e.touches[0]?.clientX;
  }

  function handleDetailTouchEnd(e: TouchEvent<HTMLDivElement>): void {
    const start = mobileDetailTouchStartX.current;
    if (start === undefined) return;
    const end = e.changedTouches[0]?.clientX ?? 0;
    if (end - start > 60) setMobileDetailOpen(false);
    mobileDetailTouchStartX.current = undefined;
  }

  function handleTreeTouchStart(e: TouchEvent<HTMLElement>): void {
    treeTouchStartRef.current = { x: e.touches[0]?.clientX ?? 0, y: e.touches[0]?.clientY ?? 0 };
  }

  function handleTreeTouchEnd(e: TouchEvent<HTMLElement>): void {
    const start = treeTouchStartRef.current;
    if (!start) return;
    const endX = e.changedTouches[0]?.clientX ?? 0;
    const endY = e.changedTouches[0]?.clientY ?? 0;
    const dx = endX - start.x;
    const dy = Math.abs(endY - start.y);
    if (dx > 70 && dy < dx) setMobileSelectorOpen(true);
    if (dx < -70 && dy < Math.abs(dx)) setMobileSelectorOpen(false);
    treeTouchStartRef.current = undefined;
  }

  const objectivesById = useMemo(
    () => new Map(snapshot.objectives.map((o) => [o.id, o])),
    [snapshot.objectives],
  );

  const keyResultsById = useMemo(
    () => new Map(snapshot.keyResults.map((kr) => [kr.id, kr])),
    [snapshot.keyResults],
  );
  const projectsById = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);

  const projectObjectives = useMemo(
    () =>
      snapshot.objectives
        .filter((o) => o.projectId === activeProject.id)
        .sort((a, b) => a.order - b.order),
    [activeProject.id, snapshot.objectives],
  );

  const projectObjectiveIds = useMemo(
    () => new Set(projectObjectives.map((o) => o.id)),
    [projectObjectives],
  );

  const rootObjectives = useMemo(
    () =>
      projectObjectives.filter(
        (o) => o.parentObjectiveId === undefined || !projectObjectiveIds.has(o.parentObjectiveId),
      ),
    [projectObjectiveIds, projectObjectives],
  );

  const childrenByParent = useMemo(() => {
    const map = new Map<string, Objective[]>();
    for (const o of projectObjectives) {
      if (!o.parentObjectiveId) continue;
      if (!projectObjectiveIds.has(o.parentObjectiveId)) continue;
      const list = map.get(o.parentObjectiveId) ?? [];
      list.push(o);
      map.set(o.parentObjectiveId, list);
    }
    for (const children of map.values()) {
      children.sort((a, b) => a.order - b.order);
    }
    return map;
  }, [projectObjectiveIds, projectObjectives]);

  const keyResultsByObjective = useMemo(() => {
    const map = new Map<string, KeyResult[]>();
    for (const kr of snapshot.keyResults) {
      const objective = objectivesById.get(kr.objectiveId);
      if (!objective || objective.projectId !== activeProject.id) continue;
      const list = map.get(kr.objectiveId) ?? [];
      list.push(kr);
      map.set(kr.objectiveId, list);
    }
    for (const krs of map.values()) {
      krs.sort((a, b) => a.order - b.order);
    }
    return map;
  }, [activeProject.id, objectivesById, snapshot.keyResults]);

  const tasksForProject = useMemo(
    () => snapshot.tasks.filter((t) => t.projectId === activeProject.id),
    [activeProject.id, snapshot.tasks],
  );

  const tasksByKrId = useMemo(() => {
    const map = new Map<string, Task[]>();
    for (const task of tasksForProject) {
      if (!task.krId) continue;
      const list = map.get(task.krId) ?? [];
      list.push(task);
      map.set(task.krId, list);
    }
    return map;
  }, [tasksForProject]);

  const allTasksByKrId = useMemo(() => {
    const map = new Map<string, Task[]>();
    for (const task of snapshot.tasks) {
      if (!task.krId || task.trashedAt !== undefined) continue;
      const list = map.get(task.krId) ?? [];
      list.push(task);
      map.set(task.krId, list);
    }
    return map;
  }, [snapshot.tasks]);

  const unlinkedTasks = useMemo(
    () => tasksForProject.filter((t) => !t.krId && t.trashedAt === undefined),
    [tasksForProject],
  );

  const tasksById = useMemo(
    () => new Map(tasksForProject.map((t) => [t.id, t])),
    [tasksForProject],
  );

  const workflowColumns = store.useWorkflowColumns();
  const okrSearchActive = hasSearchCriteria(searchQuery);
  const okrGridRows = useMemo(
    () =>
      buildOkrGridRows({
        objectives: okrSearchActive ? snapshot.objectives : projectObjectives,
        allObjectives: snapshot.objectives,
        keyResults: snapshot.keyResults,
        tasksByKrId: okrSearchActive ? allTasksByKrId : tasksByKrId,
        projectsById,
        searchQuery: okrSearchActive ? searchQuery : '',
        now: Date.now(),
      }),
    [
      allTasksByKrId,
      okrSearchActive,
      projectObjectives,
      projectsById,
      searchQuery,
      snapshot.keyResults,
      snapshot.objectives,
      tasksByKrId,
    ],
  );

  const filteredRootObjectives = useMemo(() => {
    if (statusFilter === 'all') return rootObjectives;
    return rootObjectives.filter((o) => o.status === statusFilter);
  }, [rootObjectives, statusFilter]);

  const selectedKr = selectedKrId ? keyResultsById.get(selectedKrId) : undefined;
  const selectedKrObjective = selectedKr ? objectivesById.get(selectedKr.objectiveId) : undefined;

  const selectedKrCheckIns = useMemo(
    () =>
      selectedKr
        ? snapshot.checkIns.filter(
            (ci) => ci.targetType === 'key_result' && ci.targetId === selectedKr.id,
          )
        : [],
    [selectedKr, snapshot.checkIns],
  );

  const activeObjectiveId = useMemo(() => {
    if (editor.kind === 'edit-objective') return editor.objectiveId;
    if (editor.kind === 'create-kr') return editor.objectiveId;
    if (editor.kind === 'create-objective') return editor.parentObjectiveId;
    if (editor.kind === 'edit-kr') {
      return keyResultsById.get(editor.keyResultId)?.objectiveId;
    }
    return selectedKrObjective?.id;
  }, [editor, keyResultsById, selectedKrObjective?.id]);

  const targets = useMemo(() => {
    const objectiveTargets = projectObjectives.map((o) => ({
      id: o.id,
      label: `Objective: ${o.title}`,
      targetType: 'objective' as const,
    }));
    const keyResultTargets = snapshot.keyResults
      .filter((kr) => {
        const objective = objectivesById.get(kr.objectiveId);
        return objective?.projectId === activeProject.id;
      })
      .map((kr) => ({
        id: kr.id,
        label: `KR: ${kr.title}`,
        targetType: 'key_result' as const,
      }));
    return { objectiveTargets, keyResultTargets };
  }, [activeProject.id, objectivesById, projectObjectives, snapshot.keyResults]);

  useEffect(() => {
    const pool =
      daily.targetType === 'objective' ? targets.objectiveTargets : targets.keyResultTargets;
    const nextTargetId = pool[0]?.id ?? '';
    if (!pool.some((t) => t.id === daily.targetId) && daily.targetId !== nextTargetId) {
      setDaily((prev) => ({ ...prev, targetId: nextTargetId }));
    }
  }, [daily.targetId, daily.targetType, targets.keyResultTargets, targets.objectiveTargets]);

  useEffect(() => {
    const pool =
      weekly.targetType === 'objective' ? targets.objectiveTargets : targets.keyResultTargets;
    const nextTargetId = pool[0]?.id ?? '';
    if (!pool.some((t) => t.id === weekly.targetId) && weekly.targetId !== nextTargetId) {
      setWeekly((prev) => ({ ...prev, targetId: nextTargetId }));
    }
  }, [targets.keyResultTargets, targets.objectiveTargets, weekly.targetId, weekly.targetType]);

  const toggleKrExpand = useCallback((krId: string) => {
    setExpandedKrIds((prev) => {
      const next = new Set(prev);
      if (next.has(krId)) next.delete(krId);
      else next.add(krId);
      return next;
    });
  }, []);

  const handleLinkTask = useCallback(
    (krId: string, taskId: string): void => {
      const linked = store.linkTaskToKeyResult(taskId, krId);
      if (!linked) {
        setLinkError('Task can only be linked to a key result in the same project.');
        return;
      }
      setLinkError(undefined);
      setLinkingKrId(undefined);
      setLinkTaskPick(undefined);
      setSelectedTaskId(undefined);
    },
    [store],
  );

  const renderObjectiveTree = (
    objective: Objective,
    depth = 0,
    visited = new Set<string>(),
  ): JSX.Element => {
    const keyResults = keyResultsByObjective.get(objective.id) ?? [];
    const alreadySeen = visited.has(objective.id);
    const nextVisited = new Set(visited);
    nextVisited.add(objective.id);
    const children = alreadySeen
      ? []
      : (childrenByParent.get(objective.id) ?? []).filter((c) => !nextVisited.has(c.id));
    const objectiveProgress = store.objectiveProgress(objective.id);
    const isActive = activeObjectiveId === objective.id;

    const progressColorClass =
      objective.status === 'on_track'
        ? 'bg-[color:var(--app-success)]'
        : objective.status === 'at_risk'
          ? 'bg-[color:var(--app-warning)]'
          : 'bg-[color:var(--app-danger)]';

    return (
      <li key={objective.id} className="space-y-1">
        <div
          className={`border px-3 py-2 transition ${
            isActive
              ? 'border-[color:var(--app-border-strong)] bg-[color:var(--app-accent-soft)] shadow-sm'
              : 'border-[color:var(--app-border)] bg-white/70 hover:bg-white'
          }`}
          style={{ marginLeft: `${depth * 16}px` }}
        >
          <div className="flex items-start justify-between gap-3">
            <button
              type="button"
              onClick={() => setEditor({ kind: 'edit-objective', objectiveId: objective.id })}
              className="min-w-0 flex-1 text-left"
            >
              <p className="truncate text-sm font-medium text-[color:var(--app-text)]">
                {objective.title}
              </p>
              <div className="mt-1 flex items-center gap-2">
                <StatusBadge status={objective.status} />
                <span className={mutedClass}>
                  {objective.startDate || objective.endDate
                    ? `${objective.startDate ? new Date(objective.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'} → ${objective.endDate ? new Date(objective.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}`
                    : 'No dates set'}
                </span>
              </div>
            </button>
            <div className="flex shrink-0 items-center gap-1">
              <IconActionButton
                label="Edit objective"
                onClick={() => setEditor({ kind: 'edit-objective', objectiveId: objective.id })}
              >
                <Pencil aria-hidden="true" size={13} />
              </IconActionButton>
              <IconActionButton
                label="Add sub-objective"
                onClick={() =>
                  setEditor({ kind: 'create-objective', parentObjectiveId: objective.id })
                }
              >
                <GitBranch aria-hidden="true" size={13} />
              </IconActionButton>
              <IconActionButton
                label="Add key result"
                variant="primary"
                onClick={() => setEditor({ kind: 'create-kr', objectiveId: objective.id })}
              >
                <Plus aria-hidden="true" size={13} />
              </IconActionButton>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-3 space-y-1.5">
            <ProgressBar value={objectiveProgress} colorClass={progressColorClass} />
            <div className="flex items-center justify-between gap-2">
              <p className={mutedClass}>{Math.round(objectiveProgress)}% complete</p>
              <p className={mutedClass}>
                {keyResults.length} KR{keyResults.length === 1 ? '' : 's'}
              </p>
            </div>
            {alreadySeen && (
              <p className="text-xs text-[color:var(--app-warning)]">
                Cyclic objective reference detected.
              </p>
            )}
          </div>

          {/* KR list with inline task rollup */}
          {keyResults.length > 0 && (
            <ul className="mt-3 space-y-1 border-t border-[color:var(--app-border)] pt-3">
              {keyResults.map((kr, i) => {
                const progress = store.keyResultProgress(kr.id);
                const isSelected = kr.id === selectedKrId;
                const isExpanded = expandedKrIds.has(kr.id);
                const linkedTasks = tasksByKrId.get(kr.id) ?? [];
                const isLinking = linkingKrId === kr.id;
                const doneCount = linkedTasks.filter((t) => t.completedAt).length;

                const krProgressColor =
                  kr.status === 'on_track'
                    ? 'bg-[color:var(--app-success)]'
                    : kr.status === 'at_risk'
                      ? 'bg-[color:var(--app-warning)]'
                      : kr.status === 'off_track'
                        ? 'bg-[color:var(--app-danger)]'
                        : 'bg-[color:var(--app-info)]';

                return (
                  <li key={kr.id}>
                    {/* KR row — chevron button + title button side by side */}
                    <div
                      className={`flex items-center gap-2 border px-2 py-1.5 transition ${
                        isSelected
                          ? 'border-[color:var(--app-info)] bg-[color:var(--app-info-soft)]'
                          : 'border-transparent hover:border-[color:var(--app-border)] hover:bg-white/70'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleKrExpand(kr.id)}
                        className="flex h-4 w-4 shrink-0 items-center justify-center text-[color:var(--app-text-soft)] hover:text-[color:var(--app-text)]"
                        aria-label={isExpanded ? 'Collapse tasks' : 'Expand tasks'}
                      >
                        {isExpanded ? (
                          <ChevronDown aria-hidden="true" size={13} />
                        ) : (
                          <ChevronRight aria-hidden="true" size={13} />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedKrId(kr.id)}
                        className="flex min-w-0 flex-1 items-center gap-2 text-left"
                      >
                        <span className="shrink-0 font-mono text-[10px] uppercase text-[color:var(--app-text-soft)]">
                          KR{i + 1}
                        </span>
                        <span className="flex-1 truncate text-xs text-[color:var(--app-text)]">
                          {kr.title}
                        </span>
                        <span className="hidden shrink-0 text-[10px] text-[color:var(--app-text-soft)] xl:block">
                          {formatProgressMode(kr.progressMode)}
                        </span>
                      </button>
                      {linkedTasks.length > 0 && (
                        <span className="shrink-0 font-mono text-[10px] text-[color:var(--app-text-soft)]">
                          {doneCount}/{linkedTasks.length}
                        </span>
                      )}
                      <div className="w-16 shrink-0">
                        <ProgressBar value={progress} colorClass={krProgressColor} />
                      </div>
                      <span className="w-8 shrink-0 text-right font-mono text-[11px] text-[color:var(--app-text-muted)]">
                        {Math.round(progress)}%
                      </span>
                    </div>

                    {/* Expanded inline task area */}
                    {isExpanded && (
                      <div className="ml-6 mt-0.5 space-y-0.5 border-l-2 border-[color:var(--app-info-soft)] pb-2 pl-3 pt-1">
                        {linkedTasks.length === 0 ? (
                          <p className="py-1 text-xs text-[color:var(--app-text-soft)]">
                            No tasks linked yet.
                          </p>
                        ) : (
                          <ul>
                            {linkedTasks.map((task) => (
                              <li key={task.id} className="group flex items-center gap-2 py-0.5">
                                <span
                                  className={`flex-1 text-xs ${
                                    task.completedAt
                                      ? 'text-[color:var(--app-text-soft)] line-through'
                                      : 'text-[color:var(--app-text-muted)]'
                                  }`}
                                >
                                  {task.title}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => store.unlinkTaskFromKeyResult(task.id)}
                                  className="text-[10px] text-[color:var(--app-text-soft)] opacity-0 transition hover:text-[color:var(--app-danger)] group-hover:opacity-100"
                                >
                                  unlink
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}

                        {/* Link task UI */}
                        {isLinking ? (
                          <div className="flex gap-1.5 pt-1.5">
                            <select
                              value={linkTaskPick ?? ''}
                              onChange={(e) => setLinkTaskPick(e.target.value)}
                              className="ui-select flex-1 py-1 text-xs"
                            >
                              {unlinkedTasks.length === 0 ? (
                                <option value="">No tasks available</option>
                              ) : (
                                unlinkedTasks.map((t) => (
                                  <option key={t.id} value={t.id}>
                                    {t.title}
                                  </option>
                                ))
                              )}
                            </select>
                            <button
                              type="button"
                              disabled={!linkTaskPick || unlinkedTasks.length === 0}
                              onClick={() => {
                                if (linkTaskPick) handleLinkTask(kr.id, linkTaskPick);
                              }}
                              className={primaryButtonClass}
                            >
                              Link
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setLinkingKrId(undefined);
                                setLinkError(undefined);
                              }}
                              className={secondaryButtonClass}
                            >
                              <X aria-hidden="true" size={13} />
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            title="Link task"
                            aria-label="Link task"
                            onClick={(e) => {
                              e.stopPropagation();
                              setLinkingKrId(kr.id);
                              setLinkTaskPick(unlinkedTasks[0]?.id);
                            }}
                            className={`${iconButtonClass} text-[color:var(--app-text-soft)] hover:text-[color:var(--app-info)]`}
                          >
                            <Plus aria-hidden="true" size={12} />
                          </button>
                        )}
                        {isLinking && linkError && (
                          <p className="text-xs text-[color:var(--app-danger)]">{linkError}</p>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {children.length > 0 && (
          <ul className="space-y-2">
            {children.map((child) => renderObjectiveTree(child, depth + 1, nextVisited))}
          </ul>
        )}
      </li>
    );
  };

  const selectedTask = selectedTaskId ? tasksById.get(selectedTaskId) : undefined;

  const renderEditorPanel = (): JSX.Element => {
    if (editor.kind === 'none') {
      if (selectedTask) {
        return (
          <OkrTaskDetail
            task={selectedTask}
            projectObjectives={projectObjectives}
            keyResultsByObjective={keyResultsByObjective}
            workflowColumns={workflowColumns}
            linkError={linkError}
            onLink={(krId) => handleLinkTask(krId, selectedTask.id)}
            onUnlink={() => store.unlinkTaskFromKeyResult(selectedTask.id)}
            onClose={() => setSelectedTaskId(undefined)}
            store={store}
          />
        );
      }
      if (!selectedKr || !selectedKrObjective) return <></>;
      return (
        <div className={`${shellCardClass} p-4`}>
          <div className="space-y-2">
            <h2 className={titleClass}>{selectedKr.title}</h2>
            <div className="space-y-3 pt-2">
              <div className="grid gap-2 sm:grid-cols-3">
                <div className={`${innerCardClass} p-2.5`}>
                  <p className={labelClass}>Objective</p>
                  <p className="mt-1 text-xs font-medium text-[color:var(--app-text)] line-clamp-2">
                    {selectedKrObjective.title}
                  </p>
                </div>
                <div className={`${innerCardClass} p-2.5`}>
                  <p className={labelClass}>Mode</p>
                  <p className="mt-1 text-xs font-medium text-[color:var(--app-text)]">
                    {formatProgressMode(selectedKr.progressMode)}
                  </p>
                </div>
                <div className={`${innerCardClass} p-2.5`}>
                  <p className={labelClass}>Progress</p>
                  <p className="mt-1 text-xs font-medium text-[color:var(--app-text)]">
                    {Math.round(store.keyResultProgress(selectedKr.id))}%
                  </p>
                </div>
              </div>
              {selectedKr.description && (
                <div className={`${innerCardClass} p-2.5`}>
                  <p className={labelClass}>Description</p>
                  <p className="mt-1 text-xs text-[color:var(--app-text-muted)]">
                    {selectedKr.description}
                  </p>
                </div>
              )}
              <div className="flex justify-end">
                <IconActionButton
                  label="Edit key result"
                  onClick={() => {
                    setEditor({ kind: 'edit-kr', keyResultId: selectedKr.id });
                  }}
                >
                  <Pencil aria-hidden="true" size={13} />
                </IconActionButton>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (editor.kind === 'create-objective') {
      const parentObjective = editor.parentObjectiveId
        ? objectivesById.get(editor.parentObjectiveId)
        : undefined;
      return (
        <ObjectiveEditor
          title={
            parentObjective ? `New sub-objective under ${parentObjective.title}` : 'New objective'
          }
          submitLabel="Create objective"
          onCancel={() => setEditor({ kind: 'none' })}
          onSubmit={(input) => {
            const payload = {
              ...input,
              projectId: activeProject.id,
              ...(editor.parentObjectiveId !== undefined
                ? { parentObjectiveId: editor.parentObjectiveId }
                : {}),
            };
            store.createObjective(payload);
            setEditor({ kind: 'none' });
          }}
        />
      );
    }

    if (editor.kind === 'edit-objective') {
      const objective = objectivesById.get(editor.objectiveId);
      if (!objective) return <></>;
      return (
        <ObjectiveEditor
          title={`Edit objective: ${objective.title}`}
          initial={objective}
          submitLabel="Save objective"
          onCancel={() => setEditor({ kind: 'none' })}
          onSubmit={(input) => {
            store.updateObjective(objective.id, input);
            setEditor({ kind: 'none' });
          }}
        />
      );
    }

    if (editor.kind === 'create-kr') {
      const objective = objectivesById.get(editor.objectiveId);
      if (!objective) return <></>;
      return (
        <KeyResultEditor
          title={`New KR for ${objective.title}`}
          submitLabel="Create key result"
          onCancel={() => setEditor({ kind: 'none' })}
          onSubmit={(input) => {
            const keyResult = store.createKeyResult({ ...input, objectiveId: objective.id });
            if (keyResult) setSelectedKrId(keyResult.id);
            setEditor({ kind: 'none' });
          }}
        />
      );
    }

    const keyResult = keyResultsById.get(editor.keyResultId);
    if (!keyResult) return <></>;
    return (
      <KeyResultEditor
        title={`Edit KR: ${keyResult.title}`}
        initial={keyResult}
        submitLabel="Save key result"
        onCancel={() => setEditor({ kind: 'none' })}
        onSubmit={(input) => {
          store.updateKeyResult(keyResult.id, input);
          setEditor({ kind: 'none' });
        }}
      />
    );
  };

  const viewLabel: Record<OkrView, string> = {
    list: 'List',
    grid: 'Grid',
    map: 'Map',
    timeline: 'Timeline',
  };
  const viewIcon: Record<OkrView, JSX.Element> = {
    list: <List aria-hidden="true" size={14} />,
    grid: <LayoutGrid aria-hidden="true" size={14} />,
    map: <MapIcon aria-hidden="true" size={14} />,
    timeline: <Rows3 aria-hidden="true" size={14} />,
  };

  return (
    <div className="relative flex h-full min-h-0 flex-col overflow-hidden text-[color:var(--app-text)]">
      {/* Mobile project selector drawer */}
      <MobileOkrSelector
        open={mobileSelectorOpen}
        projects={projects}
        activeProjectId={activeProject.id}
        onSelect={(projectId) => {
          store.setActiveProject(projectId);
          setMobileSelectorOpen(false);
        }}
      />

      {/* Mobile header row — hidden on md+ */}
      <div className="mobile-standalone-safe-top shrink-0 border-b border-[color:var(--app-border)] px-4 pb-3 md:hidden">
        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label="Open project selector"
            data-testid="mobile-okr-menu-button"
            onClick={() => setMobileSelectorOpen((open) => !open)}
            className="ui-button ui-button-subtle h-10 w-10 px-0"
          >
            <Menu aria-hidden="true" size={18} />
          </button>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-lg font-semibold tracking-tight text-[color:var(--app-text)]">
              OKRs
            </h2>
            <p className="text-xs text-[color:var(--app-text-muted)]">{activeProject.name}</p>
          </div>
          <button
            type="button"
            onClick={() => setEditor({ kind: 'create-objective' })}
            className="ui-button ui-button-primary h-10 gap-1.5 px-3 text-xs"
          >
            <Plus aria-hidden="true" size={14} />
            New
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 max-md:gap-0">
        {/* Desktop header — hidden on mobile */}
        <header className={`${shellCardClass} p-4 max-md:hidden`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-1">
              <p className={labelClass}>OKRs · {activeProject.name}</p>
              <h1 className="text-xl font-semibold tracking-tight text-[color:var(--app-text)]">
                Objectives &amp; key results
              </h1>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* View switcher */}
              <div className="flex rounded-md border border-[color:var(--app-border)] bg-[color:var(--app-panel-muted)] p-0.5">
                {(['list', 'grid', 'map', 'timeline'] as OkrView[]).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setView(v)}
                    className={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium transition ${
                      view === v
                        ? 'bg-[color:var(--app-panel-strong)] text-[color:var(--app-text)] shadow-sm'
                        : 'text-[color:var(--app-text-muted)] hover:text-[color:var(--app-text)]'
                    }`}
                  >
                    {viewIcon[v]}
                    {viewLabel[v]}
                  </button>
                ))}
              </div>
              <button
                type="button"
                title="Add top-level objective"
                aria-label="Add top-level objective"
                onClick={() => setEditor({ kind: 'create-objective' })}
                className="ui-button ui-button-primary h-9 w-9 px-0"
              >
                <Plus aria-hidden="true" size={14} />
              </button>
            </div>
          </div>

          {/* Status filter + stats — list view only */}
          {view === 'list' && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {(['all', ...STATUS_OPTIONS] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatusFilter(s)}
                  className={`rounded px-3 py-1 text-xs font-medium transition ${
                    statusFilter === s
                      ? 'bg-[color:var(--app-accent-soft)] text-[color:var(--app-accent-text)]'
                      : 'border border-[color:var(--app-border)] bg-white/70 text-[color:var(--app-text-muted)] hover:bg-white hover:text-[color:var(--app-text)]'
                  }`}
                >
                  {s === 'all' ? 'All' : formatStatus(s)}
                </button>
              ))}
              <span className="ui-chip ml-auto">
                {projectObjectives.length} obj ·{' '}
                {
                  snapshot.keyResults.filter(
                    (kr) => objectivesById.get(kr.objectiveId)?.projectId === activeProject.id,
                  ).length
                }{' '}
                KRs
              </span>
            </div>
          )}
        </header>

        {okrSearchActive && (
          <div className="flex flex-wrap items-center gap-2 max-md:border-b max-md:border-[color:var(--app-border)] max-md:px-4 max-md:py-3">
            <span className="ui-chip ui-chip-info">
              {okrGridRows.length} OKR search result{okrGridRows.length === 1 ? '' : 's'}
            </span>
            <button
              type="button"
              onClick={onClearSearch}
              className="ui-button ui-button-subtle h-8 px-3 text-xs"
            >
              Clear
            </button>
          </div>
        )}

        {(view === 'grid' || okrSearchActive) && (
          <div className="grid min-h-0 flex-1 gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
            <div className={`${shellCardClass} min-h-0 overflow-hidden max-md:rounded-none`}>
              <OkrGridView
                rows={okrGridRows}
                searchActive={okrSearchActive}
                getObjectiveProgress={(id) => store.objectiveProgress(id)}
                getKrProgress={(id) => store.keyResultProgress(id)}
                onSelectObjective={(objectiveId) =>
                  setEditor({ kind: 'edit-objective', objectiveId })
                }
                onSelectKr={(keyResultId) => setSelectedKrId(keyResultId)}
                onOpenTask={onOpenTask}
              />
            </div>
            <ScrollViewport className="min-h-0 max-xl:hidden" viewportClassName="h-full">
              <aside className="space-y-4">
                <section className="space-y-4">{renderEditorPanel()}</section>
              </aside>
            </ScrollViewport>
          </div>
        )}

        {/* List view */}
        {view === 'list' && !okrSearchActive && (
          <div className="relative grid min-h-0 flex-1 gap-4 xl:grid-cols-[300px_minmax(0,1fr)_360px]">
            {/* Left: objective tree */}
            <section
              data-testid="okr-tree-pane"
              onTouchStart={handleTreeTouchStart}
              onTouchEnd={handleTreeTouchEnd}
              className={`${shellCardClass} flex min-h-0 flex-col overflow-hidden p-0 xl:col-span-2 transition-transform duration-200 ease-out max-md:rounded-none max-md:border-0 max-md:shadow-none ${
                mobileDetailOpen ? 'max-md:hidden' : ''
              } ${mobileSelectorOpen ? 'max-md:mobile-task-list-shift' : ''}`}
              style={{ borderRadius: 0 }}
            >
              <div className="toolbar-strip">
                <SectionHeading
                  title="Objectives"
                  action={<span className="ui-chip px-3 py-1">{rootObjectives.length} root</span>}
                />
              </div>

              <ScrollViewport
                data-testid="okr-scope-scroll"
                className="min-h-0 flex-1"
                viewportClassName="h-full overscroll-contain touch-pan-y max-md:absolute max-md:inset-0 max-md:h-auto"
              >
                {filteredRootObjectives.length === 0 ? (
                  <div className="surface-empty border-0 border-t p-4">
                    {rootObjectives.length === 0
                      ? 'No objectives yet for this project.'
                      : 'No objectives match the current filter.'}
                  </div>
                ) : (
                  <ul className="space-y-3">
                    {filteredRootObjectives.map((o) => renderObjectiveTree(o))}
                  </ul>
                )}

                {unlinkedTasks.length > 0 && (
                  <div className="border-t border-[color:var(--app-border)] pt-4">
                    <p className={`${labelClass} mb-2 px-3`}>
                      Unlinked tasks ({unlinkedTasks.length})
                    </p>
                    <ul className="space-y-0.5">
                      {unlinkedTasks.map((task) => (
                        <li key={task.id}>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedTaskId(task.id);
                              setSelectedKrId(undefined);
                              setEditor({ kind: 'none' });
                            }}
                            className={`w-full px-3 py-2 text-left text-xs transition ${
                              selectedTaskId === task.id
                                ? 'bg-[color:var(--app-accent-soft)] text-[color:var(--app-text)]'
                                : 'text-[color:var(--app-text-muted)] hover:bg-white/70 hover:text-[color:var(--app-text)]'
                            } ${task.completedAt ? 'line-through opacity-60' : ''}`}
                          >
                            {task.title}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </ScrollViewport>
            </section>

            {/* Right: editor + inspector — KR check-ins */}
            <div
              data-testid="mobile-okr-detail-screen"
              data-open={mobileDetailOpen || undefined}
              onTouchStart={handleDetailTouchStart}
              onTouchEnd={handleDetailTouchEnd}
              className={`min-h-0 max-md:absolute max-md:inset-0 max-md:z-20 max-md:flex max-md:flex-col max-md:overflow-hidden max-md:bg-[color:var(--app-canvas)] max-md:transition-transform max-md:duration-200 max-md:ease-out ${
                mobileDetailOpen ? 'max-md:translate-x-0' : 'max-md:translate-x-full'
              }`}
            >
              <div className="shrink-0 border-b border-[color:var(--app-border)] bg-[color:var(--app-mobile-bar-bg)] px-4 py-3 backdrop-blur md:hidden">
                <button
                  type="button"
                  data-testid="mobile-okr-back"
                  onClick={() => setMobileDetailOpen(false)}
                  className={secondaryButtonClass}
                >
                  <ArrowLeft aria-hidden="true" size={14} />
                  Back to objectives
                </button>
              </div>
              <ScrollViewport
                className="min-h-0 flex-1"
                viewportClassName="h-full overscroll-contain touch-pan-y max-md:absolute max-md:inset-0 max-md:h-auto"
              >
                <aside className="space-y-4">
                  <section className="space-y-4">{renderEditorPanel()}</section>

                  {selectedKr ? (
                    <div className={`${shellCardClass} space-y-4 p-4`}>
                      <SectionHeading
                        title={selectedKr.title}
                        action={
                          <IconActionButton
                            label="Edit key result"
                            onClick={() =>
                              setEditor({ kind: 'edit-kr', keyResultId: selectedKr.id })
                            }
                          >
                            <Pencil aria-hidden="true" size={13} />
                          </IconActionButton>
                        }
                      />

                      {/* Quick check-in for selected KR */}
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (!selectedKr || !daily.note) return;
                          store.createCheckIn({
                            cadence: 'daily',
                            targetType: 'key_result',
                            targetId: selectedKr.id,
                            note: daily.note,
                            ...(daily.statusUpdate ? { statusUpdate: daily.statusUpdate } : {}),
                            ...(daily.confidenceUpdate !== ''
                              ? { confidenceUpdate: Number(daily.confidenceUpdate) }
                              : {}),
                          });
                          setDaily((prev) => ({ ...prev, note: '', confidenceUpdate: '' }));
                        }}
                        className={`${innerCardClass} space-y-3 p-3`}
                      >
                        <p className={labelClass}>Daily check-in</p>
                        <textarea
                          value={daily.note}
                          onChange={(e) => setDaily((prev) => ({ ...prev, note: e.target.value }))}
                          placeholder="What moved today?"
                          rows={2}
                          className={fieldClass}
                        />
                        <div className="flex gap-2">
                          <select
                            value={daily.statusUpdate}
                            onChange={(e) =>
                              setDaily((prev) => ({
                                ...prev,
                                statusUpdate: e.target.value as '' | ObjectiveStatusT,
                              }))
                            }
                            className={`${selectClass} flex-1`}
                          >
                            <option value="">No status change</option>
                            {STATUS_OPTIONS.map((s) => (
                              <option key={s} value={s}>
                                {formatStatus(s)}
                              </option>
                            ))}
                          </select>
                          <button type="submit" className={primaryButtonClass}>
                            Save
                          </button>
                        </div>
                      </form>

                      {/* Recent check-ins for this KR */}
                      <div className={`${innerCardClass} p-3`}>
                        <p className={labelClass}>Recent check-ins</p>
                        <div className="mt-3">
                          {selectedKrCheckIns.length === 0 ? (
                            <p className="text-sm text-[color:var(--app-text-muted)]">
                              No check-ins for this KR yet.
                            </p>
                          ) : (
                            <ul className="space-y-2">
                              {selectedKrCheckIns.slice(0, 5).map((ci) => (
                                <li
                                  key={ci.id}
                                  className="rounded-lg border border-[color:var(--app-border)] bg-white/70 p-3 text-sm"
                                >
                                  <p className="text-[color:var(--app-text-muted)]">{ci.note}</p>
                                  <p className="mt-1 text-xs text-[color:var(--app-text-soft)]">
                                    {ci.cadence} check-in
                                    {ci.statusUpdate ? ` · ${formatStatus(ci.statusUpdate)}` : ''}
                                  </p>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className={`${shellCardClass} p-4`}>
                      <h2 className={titleClass}>Select a key result or task</h2>
                      <p className="mt-2 text-sm text-[color:var(--app-text-muted)]">
                        Pick a key result to log a check-in, or select an unlinked task to link it
                        to an objective.
                      </p>
                    </div>
                  )}

                  {/* Full check-in forms */}
                  <CheckInCard
                    title="Daily micro check-in"
                    cadence="daily"
                    state={daily}
                    targets={targets}
                    onChange={setDaily}
                    onSubmit={() => {
                      if (!daily.targetId) return;
                      store.createCheckIn({
                        cadence: 'daily',
                        targetType: daily.targetType,
                        targetId: daily.targetId,
                        note: daily.note,
                        ...(daily.statusUpdate ? { statusUpdate: daily.statusUpdate } : {}),
                        ...(daily.targetType === 'key_result' && daily.confidenceUpdate !== ''
                          ? { confidenceUpdate: Number(daily.confidenceUpdate) }
                          : {}),
                      });
                      setDaily((prev) => ({ ...prev, note: '', confidenceUpdate: '' }));
                    }}
                  />

                  <CheckInCard
                    title="Weekly guided review"
                    cadence="weekly"
                    state={weekly}
                    targets={targets}
                    onChange={setWeekly}
                    onSubmit={() => {
                      if (!weekly.targetId) return;
                      store.createCheckIn({
                        cadence: 'weekly',
                        targetType: weekly.targetType,
                        targetId: weekly.targetId,
                        note: weekly.note,
                        ...(weekly.blockers ? { blockers: weekly.blockers } : {}),
                        ...(weekly.nextSteps ? { nextSteps: weekly.nextSteps } : {}),
                        ...(weekly.statusUpdate ? { statusUpdate: weekly.statusUpdate } : {}),
                        ...(weekly.targetType === 'key_result' && weekly.confidenceUpdate !== ''
                          ? { confidenceUpdate: Number(weekly.confidenceUpdate) }
                          : {}),
                      });
                      setWeekly((prev) => ({
                        ...prev,
                        note: '',
                        blockers: '',
                        nextSteps: '',
                        confidenceUpdate: '',
                      }));
                    }}
                  />
                </aside>
              </ScrollViewport>
            </div>
          </div>
        )}

        {/* Map view */}
        {view === 'map' && !okrSearchActive && (
          <div className={`${shellCardClass} min-h-0 flex-1 overflow-hidden`}>
            <OkrStrategyMap
              rootObjectives={rootObjectives}
              objectivesById={objectivesById}
              childrenByParent={childrenByParent}
              keyResultsByObjective={keyResultsByObjective}
              getObjectiveProgress={(id) => store.objectiveProgress(id)}
              getKrProgress={(id) => store.keyResultProgress(id)}
            />
          </div>
        )}

        {/* Timeline view */}
        {view === 'timeline' && !okrSearchActive && (
          <div className={`${shellCardClass} min-h-0 flex-1 overflow-hidden`}>
            <OkrTimeline
              rootObjectives={rootObjectives}
              childrenByParent={childrenByParent}
              keyResultsByObjective={keyResultsByObjective}
              tasksByKrId={tasksByKrId}
              getObjectiveProgress={(id) => store.objectiveProgress(id)}
              getKrProgress={(id) => store.keyResultProgress(id)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
